import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router'
import { useState } from 'react'
import { toast } from 'sonner'

import { Page } from '@/components/page'
import { LoadingPage } from '@/components/skeletons'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Spinner } from '@/components/ui/spinner'
import {
  finishRun,
  planKey,
  runKey,
  runsKey,
  startRun,
  useBoard,
  usePlacements,
  usePlan,
  useRun,
  useRuns,
  type RunView,
  type Side,
} from '@/features/assembly/api'
import { RunScreen } from '@/features/assembly/RunScreen'
import { fetchBuildCheck, useProject } from '@/features/projects/api'

/** Every state of the assistant is a link: that is the desk-to-shelf handover. */
interface AssemblySearch {
  run?: string
  step?: number
  side?: Side
}

function parseSearch(search: Record<string, unknown>): AssemblySearch {
  const step = Number(search.step)
  return {
    run: typeof search.run === 'string' && search.run ? search.run : undefined,
    // Anything meaning "the default" stays undefined, or it gets spelled out
    // in every link.
    step: Number.isInteger(step) && step > 0 ? step : undefined,
    side: search.side === 'bottom' ? ('bottom' as const) : undefined,
  }
}

export const Route = createFileRoute('/projects/$projectId/assembly')({
  validateSearch: parseSearch,
  component: AssemblyPage,
})

function AssemblyPage() {
  const { projectId } = Route.useParams()
  const search = Route.useSearch()
  const navigate = useNavigate({ from: Route.fullPath })
  const queryClient = useQueryClient()

  const project = useProject(projectId)
  const board = useBoard(projectId)
  const plan = usePlan(projectId)
  const placements = usePlacements(projectId)
  const runs = useRuns(projectId)
  const [finishing, setFinishing] = useState(false)

  // The run named in the URL, else the newest one still open.
  const open = runs.data?.find((run) => run.status === 'in_progress')
  const runId = search.run ?? open?.id
  const run = useRun(runId)

  const begin = useMutation({
    mutationFn: () => startRun(projectId),
    onSuccess: async (started) => {
      await queryClient.invalidateQueries({ queryKey: runsKey(projectId) })
      void navigate({ search: (old) => ({ ...old, run: started.id }), replace: true })
    },
    onError: (error) => toast.error(error instanceof Error ? error.message : 'Could not start'),
  })

  if (project.isPending || board.isPending || plan.isPending || runs.isPending) {
    return (
      <Page title="Assembly">
        <LoadingPage label="Loading the plan" />
      </Page>
    )
  }

  const title = project.data ? `Assembling ${project.data.name}` : 'Assembly'

  if (!board.data || !plan.data) {
    return (
      <Page title={title}>
        <Alert>
          <AlertTitle>This project has no board yet</AlertTitle>
          <AlertDescription>
            Upload its Gerber archive and position file on the project page first.
          </AlertDescription>
        </Alert>
        <Button asChild className="mt-4">
          <Link to="/projects/$projectId" params={{ projectId }}>
            Back to the project
          </Link>
        </Button>
      </Page>
    )
  }

  const side: Side = search.side ?? 'top'
  const step = Math.min((search.step ?? 1) - 1, Math.max(plan.data.clusters.length - 1, 0))

  const setStep = (next: number) =>
    navigate({ search: (old) => ({ ...old, step: next + 1 }), replace: true })
  const setSide = (next: Side) =>
    navigate({
      search: (old) => ({ ...old, side: next === 'top' ? undefined : next }),
      replace: true,
    })

  return (
    <Page
      title={title}
      breadcrumb={
        <nav aria-label="Project">
          <Link to="/projects" className="hover:text-steel-700">
            Projects
          </Link>
          {' / '}
          <Link to="/projects/$projectId" params={{ projectId }} className="hover:text-steel-700">
            {project.data?.name ?? 'Project'}
          </Link>
        </nav>
      }
      actions={
        run.data?.status === 'in_progress' ? (
          <Button variant="secondary" onClick={() => setFinishing(true)}>
            Finish…
          </Button>
        ) : null
      }
    >
      {run.data && run.data.status === 'in_progress' ? (
        <RunScreen
          board={board.data}
          plan={plan.data}
          placements={placements.data ?? []}
          run={run.data}
          step={step}
          side={side}
          onStepChange={setStep}
          onSideChange={setSide}
          onFinish={() => setFinishing(true)}
        />
      ) : (
        <StartScreen
          runs={runs.data ?? []}
          current={run.data ?? null}
          starting={begin.isPending}
          onStart={() => begin.mutate()}
          onOpen={(id) => navigate({ search: (old) => ({ ...old, run: id }), replace: true })}
        />
      )}

      {run.data && (
        <FinishDialog
          projectId={projectId}
          run={run.data}
          open={finishing}
          onOpenChange={setFinishing}
        />
      )}
    </Page>
  )
}

/** No run open: offer to start one, and list what came before. */
function StartScreen({
  runs,
  current,
  starting,
  onStart,
  onOpen,
}: {
  runs: RunView[]
  current: RunView | null
  starting: boolean
  onStart: () => void
  onOpen: (id: string) => void
}) {
  return (
    <div className="flex flex-col gap-4">
      {current && current.status !== 'in_progress' && (
        <Alert>
          <AlertTitle>This run is {current.status}</AlertTitle>
          <AlertDescription>
            {current.placed.length} part{current.placed.length === 1 ? '' : 's'} were placed.
            {current.build_id && ' The stock was booked out as a build.'}
          </AlertDescription>
        </Alert>
      )}

      <Button className="min-h-11 self-start" disabled={starting} onClick={onStart}>
        {starting && <Spinner />}
        Start assembling a board
      </Button>

      {runs.length > 0 && (
        <section aria-label="Earlier runs" className="flex flex-col gap-2">
          <h4 className="text-xl">Runs</h4>
          <ul className="flex flex-col gap-2">
            {runs.map((run) => (
              <li
                key={run.id}
                className="border-divider flex flex-wrap items-center gap-3 border p-3"
              >
                <Badge variant={run.status === 'in_progress' ? 'default' : 'secondary'}>
                  {run.status === 'in_progress' ? 'open' : run.status}
                </Badge>
                <span className="text-sm">
                  {run.placed.length} placed
                  {run.build_id && ' · stock booked out'}
                </span>
                <Button
                  variant="ghost"
                  size="sm"
                  className="ml-auto"
                  onClick={() => onOpen(run.id)}
                >
                  Open
                </Button>
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  )
}

/**
 * Finish a run, and offer to book the stock out as a build of one — reusing
 * the build check's own suggestion, so the allocation is the same one the
 * build wizard would make.
 */
function FinishDialog({
  projectId,
  run,
  open,
  onOpenChange,
}: {
  projectId: string
  run: RunView
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const queryClient = useQueryClient()
  const navigate = useNavigate({ from: Route.fullPath })
  const [recordBuild, setRecordBuild] = useState(true)

  const finish = useMutation({
    mutationFn: async () => {
      let allocations: { bom_entry_id: string; lot_id: string; amount: string }[] = []
      if (recordBuild) {
        const check = await fetchBuildCheck(projectId, '1')
        allocations = check.lines.flatMap((line) =>
          line.suggested.map((suggestion) => ({
            bom_entry_id: line.bom_entry_id,
            lot_id: suggestion.lot_id,
            amount: suggestion.amount,
          })),
        )
      }
      return finishRun(run.id, { record_build: recordBuild, allocations })
    },
    onSuccess: async (finished) => {
      toast.success(finished.build_id ? 'Finished, and the stock was booked out' : 'Finished')
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: runKey(run.id) }),
        queryClient.invalidateQueries({ queryKey: runsKey(projectId) }),
        queryClient.invalidateQueries({ queryKey: planKey(projectId) }),
        queryClient.invalidateQueries({ queryKey: ['project', projectId] }),
      ])
      onOpenChange(false)
      void navigate({ search: (old) => ({ ...old, step: undefined }), replace: true })
    },
    onError: (error) =>
      toast.error(error instanceof Error ? error.message : 'Could not finish the run'),
  })

  if (!open) return null

  return (
    <div
      role="dialog"
      aria-label="Finish the run"
      className="border-divider bg-ground fixed inset-x-4 bottom-4 z-50 flex flex-col gap-3 border p-4 shadow-lg sm:inset-x-auto sm:right-6 sm:w-96"
    >
      <h3 className="text-xl">Finish this run</h3>
      <p className="text-sm text-neutral-600">
        {run.placed.length} part{run.placed.length === 1 ? '' : 's'} placed.
      </p>
      <label className="flex items-start gap-2 text-sm">
        <Checkbox
          checked={recordBuild}
          onCheckedChange={(checked) => setRecordBuild(checked === true)}
        />
        <span>
          Take the parts out of stock, as a build of one. The split is the one the build wizard
          would suggest: oldest lots first.
        </span>
      </label>
      <div className="flex gap-2">
        <Button variant="secondary" className="min-h-11" onClick={() => onOpenChange(false)}>
          Cancel
        </Button>
        <Button
          className="min-h-11 flex-1"
          disabled={finish.isPending}
          onClick={() => finish.mutate()}
        >
          {finish.isPending && <Spinner />}
          Finish
        </Button>
      </div>
    </div>
  )
}
